/* ============================================================
   SAM-198 (mock 7b) — "New business" as its own full page
   (/business/new), opened from "New business" on the business list.

   Identity (name, logo, email, phone) and where it appears in the app
   (category + subcategory of the business, from /business_category and
   /business_type). Schedule, team and services are configured on the
   business page after creating it, so this form stays short. Create uses
   the existing `useCreateBusiness` (POST /crud/business) and then opens
   the new business's page.

   New file under the BaseUI/Styletron freeze (SAM-175): Tailwind + tokens.
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCreateBusiness } from '../../hooks/useBusinessCrud';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { Modal } from '../../components/ui/Modal';
import { toaster } from '../../components/ui/toast';
import { crudLocationClient, linkNewBusiness, locationErrorText } from './location/locationSave';
import {
  EMPTY_NEW_BUSINESS, createdBusinessId, isNewBusinessDirty, newBusinessIssues, newBusinessPayload,
  type NewBusinessForm,
} from './newBusiness';
import { NewBusinessFields, newBusinessCard as card } from './NewBusinessFields';

export const NewBusinessPage: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useUserPermissions();
  const createBusiness = useCreateBusiness();

  const [form, setForm] = useState<NewBusinessForm>(EMPTY_NEW_BUSINESS);
  const [touched, setTouched] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const issues = newBusinessIssues(form);
  const dirty = isNewBusinessDirty(form);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirty || createBusiness.isSuccess) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, createBusiness.isSuccess]);

  if (permissions.isLoading) {
    return <div className="h-full flex items-center justify-center text-sm text-[var(--ink-500)]">Loading…</div>;
  }
  // Only Cap Cana / Xhinola admins create businesses; a business admin has just their own.
  if (!permissions.isAdmin) return <Navigate to="/business/list" replace />;

  const leave = () => navigate('/business/list');
  const onCancel = () => (dirty ? setConfirmLeave(true) : leave());

  const onCreate = () => {
    setTouched(true);
    if (issues.length) return;
    createBusiness.mutate(newBusinessPayload(form), {
      onSuccess: async (response) => {
        const id = createdBusinessId(response);
        // SAM-1237: a Cap Cana business needs its estate group linked once it exists.
        if (id) {
          try {
            await linkNewBusiness(crudLocationClient, id, form.location);
          } catch (error) {
            toaster.warning(`Business created, but its estate group was not saved: ${locationErrorText(error)} Set it in Settings.`, { autoHideDuration: 10000 });
          }
        } else {
          toaster.warning('Business created, but its estate group could not be linked. Open it from the list and set it in Settings.', { autoHideDuration: 10000 });
        }
        navigate(id ? `/business/${id}` : '/business/list', { replace: true });
      },
    });
  };

  const saving = createBusiness.isPending;

  return (
    <div className="h-full min-h-0 min-w-0 w-full flex flex-col bg-[var(--surface-2,#f5f5f5)]">
      <header className="shrink-0 bg-white border-b border-solid border-x-0 border-t-0 border-[var(--line)]">
        <div className="flex items-center gap-3 px-4 md:px-6 py-3 md:py-4 flex-wrap">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Back to businesses"
            className="w-10 h-10 shrink-0 rounded-[10px] border border-solid border-[var(--line)] bg-white flex items-center justify-center text-[var(--ink-700)] cursor-pointer"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-[18px] md:text-[20px] font-bold text-[var(--ink-900)]">New business</h1>
            <p className="text-[13px] text-[var(--ink-500)]">
              It starts inactive and hidden. Set hours, team and services on its page, then turn it on in Settings.
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="flex-1 sm:flex-none h-11 px-4 rounded-[12px] border border-solid border-[var(--line)] bg-white text-[15px] font-semibold text-[var(--ink-700)] cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onCreate}
              disabled={saving}
              className="flex-1 sm:flex-none h-11 px-[18px] rounded-[12px] border-0 bg-[var(--tenant-primary,#005488)] text-white text-[15px] font-semibold cursor-pointer inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? 'Creating…' : 'Create business'}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-5">
          <NewBusinessFields form={form} setForm={setForm} touched={touched} />

          <section aria-labelledby="nb-next" className={card}>
            <h2 id="nb-next" className="text-[16px] font-bold mb-2 text-[var(--ink-900)]">After creating it</h2>
            <ol className="pl-5 text-[14px] leading-[1.7] text-[var(--ink-700)]" style={{ listStyleType: 'decimal' }}>
              <li>Settings › Schedule: opening hours and closures.</li>
              <li>Team: add its admin and front desk.</li>
              <li>Services: what it sells.</li>
            </ol>
          </section>
        </div>
      </main>

      <Modal
        isOpen={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        title="Discard this new business?"
        footer={(
          <>
            <button type="button" className="btn ghost" style={{ color: 'var(--danger)' }} onClick={leave}>Discard</button>
            <button type="button" className="btn primary" onClick={() => setConfirmLeave(false)}>Keep editing</button>
          </>
        )}
      >
        <p className="text-[14px]" style={{ color: 'var(--ink-700)' }}>Nothing has been created yet.</p>
      </Modal>
    </div>
  );
};

export default NewBusinessPage;
